import useNeveForm from 'components/forms/NeveForm';
import strings from 'strings/strings.json';
import { Headline3, HorizontalRule } from 'components/Elements';
import useUploadRequest from 'hooks/useUploadRequest';
import useModifyRequest from 'hooks/useModifyRequest';
import React from 'react';
import { CreateFileSource, CreateAudioTaskRequest } from 'models/inputModels/CreateTaskRequest';
import ResourcesList from '../../ResourcesList';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { AudioDocument, FileDocument, FileDocumentType } from 'models/FileDocument';
import { AudioCard } from '../../FileCard';
import { AudioSelectionComponent } from '../../FileSelectionComponent';
import { CreateActivityFormProps } from './shared/CreateActivityFormProps';
import { LearningResourcesFormModal } from '../../LearningResourcesFormModal';
import { Activity, ActivityType, BuildMethod } from 'models/Activity';
import { ActivityDetailsInput, ResourcesInput } from '../shared/SharedActivityFormComponents';
import { ActivityFormCard } from '../shared/ActivityFormCard';
import BaseDetailsForm from '../shared/DetailsForm';
import { FieldErrors, useFieldArray, UseFormRegister } from 'react-hook-form';

export interface AudioTaskInput extends ActivityDetailsInput {
    resources: ResourcesInput[];
}

export default function CreateAudioTaskForm({ returnToModulesList, moduleId }: CreateActivityFormProps): JSX.Element {
    const { refetchCourse, editMode, setCourseState } = React.useContext(BuildCourseContext);

    const [audioFromLocal, setAudioFromLocal] = React.useState<File | null>();
    const [audioFromProvider, setAudioFromProvider] = React.useState<FileDocument>();
    const [linkModalOpen, setLinkModalOpen] = React.useState(false);
    const [error, setError] = React.useState('');

    const addLinkButtonRef = React.useRef<HTMLButtonElement>(null);
    const selectAudioButtonRef = React.useRef<HTMLButtonElement>(null);
    const removeAudioButtonRef = React.useRef<HTMLButtonElement>(null);

    const { upload: uploadAudio, uploading: uploadingAudio } = useUploadRequest();
    const { modifyData: createTask, loading: creatingTask } = useModifyRequest<CreateAudioTaskRequest, boolean>(
        `activities/task/audio`,
        'POST',
    );

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useNeveForm<AudioTaskInput>({
        title: '',
        description: '',
        durationInMins: null,
        resources: [],
    });

    const {
        fields: resourceFields,
        append: appendResource,
        remove: removeResource,
    } = useFieldArray({
        control,
        name: 'resources',
    });

    React.useEffect(() => {
        if (!audioFromLocal || !audioFromProvider) {
            setError('');
        }
    }, [audioFromLocal, audioFromProvider]);

    async function submit(formData: AudioTaskInput) {
        setError('');

        let existingAudioId: string | null = null;
        let audioFilePath: string | null = null;
        let audioFileName: string | null = null;
        let audioUploadDate: Date | null = null;

        if (!audioFromLocal && !audioFromProvider) {
            setError('Audio is a required field');
            selectAudioButtonRef.current?.focus();
            return;
        }
        if (audioFromProvider) {
            existingAudioId = audioFromProvider.id;
            audioFileName = audioFromProvider.filename;
            audioFilePath = audioFromProvider.s3Filepath;
            audioUploadDate = audioFromProvider.dateUpdated;
        } else if (audioFromLocal) {
            const { errors, value } = await uploadAudio(audioFromLocal);
            if ((errors && errors.length) || !value) {
                setError('Error creating task');
                return;
            }
            audioFileName = value.filename;
            audioFilePath = value.s3Key;
            audioUploadDate = new Date();
        }

        if (!editMode) {
            const request: CreateAudioTaskRequest = {
                title: formData.title,
                description: formData.description,
                durationInMins: formData.durationInMins ?? 0,
                moduleId: moduleId,
                resources: resourceFields,
                audio: audioFromProvider
                    ? {
                          type: 'existing',
                          id: audioFromProvider.id,
                          fileName: audioFromProvider.filename,
                          filePath: audioFromProvider.s3Filepath,
                      }
                    : {
                          type: 'new',
                          fileName: audioFilePath!,
                          filePath: audioFilePath!,
                      },
            };

            const { errors } = await createTask(request);
            if (errors && errors.length) {
                setError('Error creating task');
                return;
            }

            await refetchCourse();
            returnToModulesList();
            return;
        }

        setCourseState((prev) => {
            if (!prev) return;
            const moduleIndex = prev.modules.findIndex((m) => m.id === moduleId);
            if (moduleIndex === -1) return prev;

            const module = prev.modules[moduleIndex];

            const newActivityOrder =
                module.activities.length > 0 ? module.activities[module.activities.length - 1].order + 1 : 1;

            const audioDocument: AudioDocument = {
                id: existingAudioId ?? '',
                filename: audioFileName!,
                s3Filepath: audioFilePath!,
                dateUpdated: audioUploadDate!,
                documentType: FileDocumentType.AUDIO,
            };
            const newActivity: Activity = {
                id: '',
                moduleId: moduleId,
                type: ActivityType.TASK,
                buildMethod: BuildMethod.AUDIO,
                title: formData.title,
                description: formData.description,
                durationInMins: formData.durationInMins,
                content: '',
                order: newActivityOrder,
                resources: resourceFields,
                audioDocument: audioDocument,
                dateCreated: new Date(),
                questions: [],
                statuses: [],
                meetings: [],
                imageDocuments: [],
                videoDocument: null,
                generateContentStatus: null,
                taskContents: [],
            };

            const updatedModules = [...prev.modules];
            updatedModules[moduleIndex] = {
                ...module,
                activities: [...module.activities, newActivity],
            };
            return { ...prev, modules: updatedModules };
        });

        returnToModulesList();
        return;
    }

    const addResource = (newResource: ResourcesInput) => {
        appendResource(newResource);
        addLinkButtonRef.current?.focus();
    };
    const deleteResource = (index: number) => {
        removeResource(index);
        addLinkButtonRef.current?.focus();
    };
    const closeLinksModal = () => {
        setLinkModalOpen(false);
        addLinkButtonRef.current?.focus();
    };

    const setAudioFromLocalFunc = (newFile: File | null | undefined) => {
        setAudioFromLocal(newFile);
        setAudioFromProvider(undefined);
    };

    const setAudioFromProviderFunc = (audio: FileDocument) => {
        setAudioFromLocal(null);
        setAudioFromProvider(audio);
    };

    const removeAudio = () => {
        setAudioFromLocal(null);
        setAudioFromProvider(undefined);
        selectAudioButtonRef.current?.focus();
    };

    let audioName: string;
    let audioFilePath: string;
    let audioSource: CreateFileSource;

    if (audioFromLocal) {
        audioSource = 'local';
        audioName = audioFromLocal.name;
        audioFilePath = URL.createObjectURL(audioFromLocal);
    } else if (audioFromProvider) {
        audioSource = 'provider';
        audioName = audioFromProvider.filename;
        audioFilePath = audioFromProvider.s3Filepath;
    } else {
        audioSource = 'provider';
        audioName = strings.fileUpload.browseFiles;
        audioFilePath = '';
    }

    const Audio = (
        <>
            <Headline3>{strings.taskContentForm.buildMethod.audio}</Headline3>
            {audioFromLocal || audioFromProvider ? (
                <AudioCard
                    fileName={audioName}
                    filePath={audioFilePath}
                    source={audioSource}
                    disabled={uploadingAudio || creatingTask}
                    removeFile={removeAudio}
                    removeFileButtonRef={removeAudioButtonRef}
                />
            ) : (
                <AudioSelectionComponent
                    setFileFromLocal={setAudioFromLocalFunc}
                    setFileFromProvider={setAudioFromProviderFunc}
                    setFileFromLocalMetaData={() => {}}
                    disableButton={uploadingAudio || creatingTask}
                    selectFileButtonRef={selectAudioButtonRef}
                />
            )}
        </>
    );

    return (
        <>
            <ActivityFormCard
                title={strings.activityDetailsForm.instruction}
                onSubmit={handleSubmit(submit)}
                pageOne={
                    <DetailsAndLinks
                        register={register}
                        errors={errors}
                        resources={resourceFields}
                        deleteResource={deleteResource}
                        toggleModalOpen={() => setLinkModalOpen((prev) => !prev)}
                        disabled={uploadingAudio || creatingTask}
                        addLinkButtonRef={addLinkButtonRef}
                    />
                }
                pageTwo={Audio}
                submitting={uploadingAudio || creatingTask}
                error={error}
                activityType={ActivityType.TASK}
            />
            {linkModalOpen && <LearningResourcesFormModal closeModal={closeLinksModal} addResource={addResource} />}
        </>
    );
}

type DetailsAndLinksProps = {
    register: UseFormRegister<AudioTaskInput>;
    errors: FieldErrors<AudioTaskInput>;
    resources: ResourcesInput[];
    deleteResource: (index: number) => void;
    toggleModalOpen: () => void;
    disabled: boolean;
    addLinkButtonRef: React.RefObject<HTMLButtonElement>;
};

function DetailsAndLinks({
    register,
    errors,
    resources,
    deleteResource,
    toggleModalOpen,
    disabled,
    addLinkButtonRef,
}: DetailsAndLinksProps) {
    return (
        <>
            <BaseDetailsForm register={register} errors={errors} autoFocus />
            <HorizontalRule />
            <ResourcesList
                resources={resources}
                deleteResource={deleteResource}
                toggleModalOpen={toggleModalOpen}
                disabled={disabled}
                addLinkButtonRef={addLinkButtonRef}
            />
        </>
    );
}
